import { useState, useDeferredValue } from 'react';
import { Search, Copy, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AdminTabShell } from './AdminTabShell';
import {
  useAdminSearchProperties,
  useAdminPropertyDetail,
  type PropertySearchResult,
  type PropertyDetailData,
  type PropertyDetailQuality,
} from '@/hooks/admin/useAdminPropertyLookup';
import {
  TIER_LABELS,
  TIER_BADGE_CLASSES,
  PV_HEALTH_LABELS,
  PV_HEALTH_CLASSES,
  REASON_CODE_LABELS,
  formatPercent,
  formatScore,
} from '@/lib/data-quality-labels';

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="h-6 px-1.5 text-xs gap-1 text-wt-text-muted">
      {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
      {label}
    </Button>
  );
}

function PropertyResultsList({ results, selectedId, onSelect }: {
  results: PropertySearchResult[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (results.length === 0) return <p className="text-xs text-wt-text-muted py-4 text-center">No results</p>;
  return (
    <div className="space-y-1">
      {results.map(p => (
        <button
          key={p.id}
          onClick={() => onSelect(p.id)}
          className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
            selectedId === p.id ? 'bg-wt-bg-secondary border border-wt-border' : 'hover:bg-wt-bg-secondary/50'
          }`}
        >
          <div className="flex justify-between items-center">
            <span className="font-medium">{p.name}</span>
            <Badge variant="secondary" className="text-[10px]">{p.readiness_state ?? 'unknown'}</Badge>
          </div>
          <p className="text-wt-text-muted mt-0.5">{p.org_name} {p.address ? `· ${p.address}` : ''}</p>
        </button>
      ))}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div><span className="text-wt-text-muted">{label}:</span> {value ?? '—'}</div>
  );
}

function PropertyDetail({ data }: { data: PropertyDetailData }) {
  const p = data.property;
  if (!p) return <p className="text-xs text-wt-text-muted py-4">Property not found</p>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{p.name}</h3>
        <CopyButton value={p.id} label="ID" />
        <a href={`/app/property/${p.id}`} className="text-xs text-blue-400 hover:underline">View Property</a>
      </div>

      {/* Info */}
      <Card className="border-wt-border bg-wt-bg-secondary">
        <CardContent className="pt-3 pb-3 px-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <InfoRow label="Address" value={p.address} />
          <InfoRow label="Timezone" value={p.timezone} />
          <InfoRow label="Readiness" value={p.readiness_state} />
          <InfoRow label="Data Source" value={p.data_source_mode} />
          <InfoRow label="Onboarded" value={p.onboarding_complete ? 'Yes' : 'No'} />
          <InfoRow label="Billing Mode" value={p.billing_mode} />
          <InfoRow label="Battery (kWh)" value={p.battery_kwh_total?.toString()} />
          <InfoRow label="Created" value={new Date(p.created_at).toLocaleDateString()} />
        </CardContent>
      </Card>

      {/* Orgs */}
      <Card className="border-wt-border bg-wt-bg-secondary">
        <CardContent className="pt-3 pb-3 px-4 grid grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-wt-text-muted">Owner Org:</span>{' '}
            {data.owner_org ? <>{data.owner_org.name} <CopyButton value={data.owner_org.id} label="ID" /></> : '—'}
          </div>
          <div>
            <span className="text-wt-text-muted">Service Provider:</span>{' '}
            {data.service_provider_org ? <>{data.service_provider_org.name} <CopyButton value={data.service_provider_org.id} label="ID" /></> : '—'}
          </div>
        </CardContent>
      </Card>

      {/* Telemetry */}
      {data.telemetry && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardContent className="pt-3 pb-3 px-4 grid grid-cols-3 gap-3 text-xs">
            <InfoRow label="Last Telemetry" value={data.telemetry.last_telemetry_at ? new Date(data.telemetry.last_telemetry_at).toLocaleString() : 'Never'} />
            <InfoRow label="Online" value={
              <Badge variant={data.telemetry.online ? 'default' : 'destructive'} className="text-[10px]">
                {data.telemetry.online ? 'Yes' : 'No'}
              </Badge>
            } />
            <InfoRow label="Stale" value={
              <Badge variant={data.telemetry.data_stale ? 'destructive' : 'default'} className="text-[10px]">
                {data.telemetry.data_stale ? 'Yes' : 'No'}
              </Badge>
            } />
          </CardContent>
        </Card>
      )}

      {/* Data Quality */}
      <DataQualityPanel quality={data.data_quality} />

      {/* Subscription */}
      {data.subscription && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Service Subscription</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <InfoRow label="Plan" value={data.subscription.plan_name} />
            <InfoRow label="Status" value={
              <Badge variant={data.subscription.status === 'active' ? 'default' : 'destructive'} className="text-[10px]">
                {data.subscription.status}
              </Badge>
            } />
            <InfoRow label="Price" value={`$${(data.subscription.price_cents / 100).toFixed(2)}`} />
            <InfoRow label="Period End" value={data.subscription.current_period_end ? new Date(data.subscription.current_period_end).toLocaleDateString() : '—'} />
          </CardContent>
        </Card>
      )}

      {/* Gateway */}
      {data.gateway && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Gateway</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <InfoRow label="Claim Code" value={<span className="font-mono">{data.gateway.claim_code}</span>} />
            <InfoRow label="Lifecycle" value={data.gateway.lifecycle_status} />
            <InfoRow label="Status" value={
              <Badge variant={data.gateway.operational_status === 'healthy' ? 'default' : 'destructive'} className="text-[10px]">
                {data.gateway.operational_status}
              </Badge>
            } />
            <InfoRow label="Firmware" value={data.gateway.firmware_version} />
            <InfoRow label="Last Heartbeat" value={data.gateway.last_heartbeat_at ? new Date(data.gateway.last_heartbeat_at).toLocaleString() : 'Never'} />
          </CardContent>
        </Card>
      )}

      {/* Plants */}
      {data.plants.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Plants ({data.plants.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Name</th>
                <th className="text-left py-1 pr-2 font-medium">Provider</th>
                <th className="text-left py-1 pr-2 font-medium">Plant ID</th>
                <th className="text-left py-1 font-medium">Tier</th>
              </tr></thead>
              <tbody>
                {data.plants.map(pl => (
                  <tr key={pl.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{pl.display_name}</td>
                    <td className="py-1 pr-2 font-mono">{pl.provider_type}</td>
                    <td className="py-1 pr-2 font-mono text-wt-text-muted">{pl.provider_plant_id ?? '—'}</td>
                    <td className="py-1">{pl.tier ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Energy Systems */}
      {data.energy_systems.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Energy Systems ({data.energy_systems.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Name</th>
                <th className="text-left py-1 pr-2 font-medium">Provider</th>
                <th className="text-left py-1 pr-2 font-medium">Serial</th>
                <th className="text-left py-1 pr-2 font-medium">Primary</th>
                <th className="text-left py-1 font-medium">Last Poll</th>
              </tr></thead>
              <tbody>
                {data.energy_systems.map(es => (
                  <tr key={es.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{es.display_name}</td>
                    <td className="py-1 pr-2 font-mono">{es.provider_type}</td>
                    <td className="py-1 pr-2 font-mono text-wt-text-muted">{es.device_sn ?? '—'}</td>
                    <td className="py-1 pr-2">{es.is_primary ? 'Yes' : '—'}</td>
                    <td className="py-1 text-wt-text-muted">
                      {es.last_poll_at ? new Date(es.last_poll_at).toLocaleString() : 'Never'}
                      {es.last_poll_status && es.last_poll_status !== 'ok' && (
                        <Badge variant="destructive" className="text-[10px] ml-1">{es.last_poll_status}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Homeowner Users */}
      {data.homeowner_users.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Homeowner Users ({data.homeowner_users.length})</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Email</th>
                <th className="text-left py-1 pr-2 font-medium">Name</th>
                <th className="text-left py-1 font-medium">Granted</th>
              </tr></thead>
              <tbody>
                {data.homeowner_users.map(ho => (
                  <tr key={ho.user_id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{ho.email}</td>
                    <td className="py-1 pr-2">{ho.full_name ?? '—'}</td>
                    <td className="py-1 text-wt-text-muted">{new Date(ho.granted_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent Issues */}
      {data.recent_issues.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Recent Issues</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Title</th>
                <th className="text-left py-1 pr-2 font-medium">Severity</th>
                <th className="text-left py-1 pr-2 font-medium">Status</th>
                <th className="text-left py-1 font-medium">Created</th>
              </tr></thead>
              <tbody>
                {data.recent_issues.map(i => (
                  <tr key={i.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{i.title}</td>
                    <td className="py-1 pr-2"><Badge variant="outline" className="text-[10px]">{i.severity}</Badge></td>
                    <td className="py-1 pr-2"><Badge variant="secondary" className="text-[10px]">{i.status}</Badge></td>
                    <td className="py-1 text-wt-text-muted">{new Date(i.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Recent Visits */}
      {data.recent_visits.length > 0 && (
        <Card className="border-wt-border bg-wt-bg-secondary">
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-xs font-medium">Recent Visits</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <table className="w-full text-xs">
              <thead><tr className="text-wt-text-muted border-b border-wt-border">
                <th className="text-left py-1 pr-2 font-medium">Type</th>
                <th className="text-left py-1 pr-2 font-medium">Status</th>
                <th className="text-left py-1 pr-2 font-medium">Technician</th>
                <th className="text-left py-1 font-medium">Scheduled</th>
              </tr></thead>
              <tbody>
                {data.recent_visits.map(v => (
                  <tr key={v.id} className="border-b border-wt-border/50">
                    <td className="py-1 pr-2">{v.visit_type}</td>
                    <td className="py-1 pr-2"><Badge variant="secondary" className="text-[10px]">{v.status}</Badge></td>
                    <td className="py-1 pr-2">{v.technician_name ?? '—'}</td>
                    <td className="py-1 text-wt-text-muted">{v.scheduled_at ? new Date(v.scheduled_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DataQualityPanel({ quality }: { quality: PropertyDetailQuality | null }) {
  if (!quality || quality.data_quality_tier == null) {
    return (
      <Card className="border-wt-border bg-wt-bg-secondary">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-xs font-medium">Data Quality</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 text-xs text-wt-text-muted">
          Quality data not yet available. The scoring function has not run for this property.
        </CardContent>
      </Card>
    );
  }

  const tier = quality.data_quality_tier ?? 'unscored';
  const reasonLabels = (quality.quality_reason_codes ?? [])
    .map(c => REASON_CODE_LABELS[c] ?? c);

  return (
    <Card className="border-wt-border bg-wt-bg-secondary">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-xs font-medium flex items-center gap-2">
          Data Quality
          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${TIER_BADGE_CLASSES[tier] ?? TIER_BADGE_CLASSES.unscored}`}>
            {TIER_LABELS[tier] ?? tier}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <InfoRow label="Score" value={<span className="font-mono">{formatScore(quality.data_quality_score)}</span>} />
          <InfoRow label="Telemetry Coverage (7d)" value={<span className="font-mono">{formatPercent(quality.telemetry_coverage_7d)}</span>} />
          <InfoRow label="Fallback Rate (7d)" value={<span className="font-mono">{formatPercent(quality.fallback_rate_7d)}</span>} />
          <InfoRow label="PV Learning" value={
            <span className={PV_HEALTH_CLASSES[quality.pv_learning_health ?? ''] ?? 'text-wt-text-muted'}>
              {PV_HEALTH_LABELS[quality.pv_learning_health ?? ''] ?? '—'}
            </span>
          } />
        </div>
        {reasonLabels.length > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] text-wt-text-muted uppercase tracking-wider font-medium">Data Quality Issues</p>
            <ul className="space-y-0.5">
              {reasonLabels.map((label, i) => (
                <li key={i} className="text-xs text-wt-text-secondary flex items-start gap-1.5">
                  <span className="text-wt-amber-primary mt-0.5">&#x2022;</span>
                  {label}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PropertyLookupTab() {
  const [query, setQuery] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query);

  const search = useAdminSearchProperties(deferredQuery);
  const detail = useAdminPropertyDetail(selectedPropertyId);

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-wt-text-muted" />
        <Input
          placeholder="Search by property name, address, plant ID, or homeowner email..."
          value={query}
          onChange={e => { setQuery(e.target.value); setSelectedPropertyId(null); }}
          className="pl-9 text-sm"
        />
      </div>

      {deferredQuery.trim().length >= 2 && (
        <AdminTabShell isLoading={search.isLoading} error={search.error}>
          <PropertyResultsList
            results={search.data ?? []}
            selectedId={selectedPropertyId}
            onSelect={setSelectedPropertyId}
          />
        </AdminTabShell>
      )}

      {selectedPropertyId && (
        <div className="mt-4 border-t border-wt-border pt-4">
          <AdminTabShell isLoading={detail.isLoading} error={detail.error}>
            {detail.data && <PropertyDetail data={detail.data} />}
          </AdminTabShell>
        </div>
      )}
    </div>
  );
}
